import { useMemo } from 'react';
import { Alert, Box, Button, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import type {
  RadarDirectionSummary,
  ListRadarDirectionsOutput,
} from '../../../client/IvtHubClient';
import { DENIAL_HINTS, type DenialKind } from '../../../utils/authorization';
import { RadarCard } from './RadarCard';
import './RadarSection.css';

/**
 * Radar section of the STO Dashboard (DAT-9115, R17; Figma A/C/D).
 *
 * Owns the section chrome (title + 偏好设置 placeholder, explanation paragraph,
 * industry-level empty-state banner) and the topic grouping around the card
 * grid. The hook (`useRadarDirections`) + card (`RadarCard`) are the two
 * seams: this file never calls the client or renders a single card's body.
 *
 * Topic grouping. The handler returns one row per direction with
 * `topicId`+`topicTitle` already set — STANDARD_TOPIC rows carry
 * `radar_config.title`, the general bucket carries the UX string "其他 / 待归
 * 主题" (classification §13; the handler does NOT return the raw DB title
 * "全部内容"). So this file groups on `(topicId, topicTitle)` as-is and does
 * no title translation; a title mismatch in the response is the backend's
 * concern. One extra ordering constraint: the general bucket renders LAST,
 * matching Figma A/C — "资讯汇总 (未按照主题分类)" sits below the standard
 * topics. We detect the bucket by `topicTitle` starting with "其他" rather
 * than hard-coding the id, because the id is a UUID per industry and the
 * title is the UX contract the handler owns.
 *
 * Empty-state banner. Rendered only when the response carries zero
 * CANDIDATE directions. As long as any card is present, the banner is
 * suppressed — the handler returns PIPELINE_ONLY directions whose evidence
 * is pipeline needs, not news, so `emptyNewsState=NO_NEWS_FOR_INDUSTRY`
 * does not mean "nothing to show" (Xin, 2026-11-04).
 *
 * Preferences gear. Clicking it routes to the R16 Admin page
 * (`/ivt/radar-config`; Figma B 4833:16952). The caller passes
 * `onOpenPreferences`; without one, the gear renders disabled, which is the
 * state a surface mounted outside the dashboard (section preview, Storybook)
 * sees — the "coming soon" placeholder R17 shipped is gone now that R16 has
 * landed.
 */
export interface RadarSectionProps {
  state: {
    data: ListRadarDirectionsOutput | null;
    loading: boolean;
    error: string | null;
    /**
     * 403 classification mirroring {@link useAssetFlow}. The radar op is
     * ANY_MEMBER today, but a tighter authorizer or an op-specific
     * `admin-not-permitted` reason can still refuse this call while the
     * sibling STO flow succeeds — the section must then render the denial
     * panel without a reload button, since retrying a 403 only re-fails.
     * The dashboard-level `noAccess` guard reads `stoFlow.denial` and does
     * NOT catch a radar-only denial, so this branch is reachable in
     * practice.
     */
    denial: DenialKind | null;
    reload: () => void;
  };
  /** Called when a card title is clicked. R18 wires the detail modal. */
  onOpenDirection: (directionId: string) => void;
  /** Called when the preference gear is clicked. R16 wires navigation. */
  onOpenPreferences?: () => void;
}

/**
 * `其他 / 待归主题` — the UX label for the GENERAL_TOPIC bucket. The handler
 * returns this verbatim per classification §13; this constant is a MATCH
 * guard only (to tag which group renders last), not an override.
 */
const GENERAL_TOPIC_PREFIX = '其他';

interface TopicGroup {
  topicId: string;
  topicTitle: string;
  directions: RadarDirectionSummary[];
  /** True when the title names the GENERAL_TOPIC bucket (renders last). */
  isGeneral: boolean;
}

/**
 * Preserve server order inside each group, and keep the server's inter-topic
 * order for the standards (first-seen wins), with the general bucket always
 * last — Figma A/C ordering. Doing it in render keeps the hook's result
 * un-mutated and sidesteps a copy/mutate dance.
 */
function groupByTopic(rows: RadarDirectionSummary[]): TopicGroup[] {
  const byId = new Map<string, TopicGroup>();
  const order: string[] = [];
  for (const row of rows) {
    const topicId = row.topicId ?? '';
    const topicTitle = row.topicTitle ?? '';
    if (!byId.has(topicId)) {
      byId.set(topicId, {
        topicId,
        topicTitle,
        directions: [],
        isGeneral: topicTitle.startsWith(GENERAL_TOPIC_PREFIX),
      });
      order.push(topicId);
    }
    byId.get(topicId)!.directions.push(row);
  }
  const groups = order.map((id) => byId.get(id)!);
  // Stable partition: standards keep server order, general bucket last.
  return [
    ...groups.filter((g) => !g.isGeneral),
    ...groups.filter((g) => g.isGeneral),
  ];
}

export function RadarSection({ state, onOpenDirection, onOpenPreferences }: RadarSectionProps) {
  const { data, loading, error, denial, reload } = state;
  const rows = data?.directions ?? [];
  const groups = useMemo(() => groupByTopic(rows), [rows]);

  return (
    <div className="radar-section">
      <div className="radar-section__header">
        <Box variant="h2" fontSize="heading-l" fontWeight="bold">
          资产雷达 —— 未来可能出现的 Asset 方向
        </Box>
        {/* Preferences gear — Figma shows a filled "偏好设置" chip in the
            top-right; R16 wires the admin page. Rendered disabled when the
            caller does not pass a handler so an admin-less user does not see
            an active control. */}
        <Button
          iconName="settings"
          variant="normal"
          disabled={!onOpenPreferences}
          onClick={onOpenPreferences}
        >
          偏好设置
        </Button>
      </div>

      {/* Explanation paragraph. Figma A/C render this between the header and
          the groups; D omits it when the empty banner is shown. We render it
          in both cases so the semantics the paragraph names (keyword rules +
          general-bucket catch-all) do not vanish once a user drops to the
          empty state. */}
      <Box variant="small" color="text-body-secondary" fontSize="body-s">
        <span className="radar-section__explain">
          全部内容:正向词让命中的内容排得更前(不决定分类);负向词让命中的内容不显示。
          每个分类:关键词既决定内容归到这一类,也让它在这一类里排得更前;负向词只对这一类的内容生效;资讯提取规则告诉大模型从这一类的新闻 / 商机里抽什么,方向归纳规则告诉它怎么把抽出的需求聚成方向。
          想在所有分类里都加权、又不想把内容拉进某一类的词(如「学术会议」),放在「全部内容」的正向里。没有设分类时,只有「全部内容」这一组生效。
        </span>
      </Box>

      {loading && !data && !denial && (
        <div className="radar-section__loading">
          <Spinner />
          <span>加载资产雷达…</span>
        </div>
      )}

      {/* Denial takes precedence over `error`: a 403 sets both, and the denial
          surface is the specific one (no reload button, DENIAL_HINTS copy),
          same split DashboardPage's page-level `noAccess` makes. The retry
          button on the error branch below would 403 again on every click. */}
      {denial && (
        <Alert type="info" header="无权访问资产雷达">
          {DENIAL_HINTS[denial]}
        </Alert>
      )}

      {error && !loading && !denial && (
        <Alert
          type="error"
          header="无法加载资产雷达"
          action={<Button onClick={reload}>重试</Button>}
        >
          {error}
        </Alert>
      )}

      {data && rows.length === 0 && !denial && (
        <Alert type="info">
          暂无资讯,你可以点击"偏好设置"按钮,按照行业预设你关注的主题,您设置的内容,会在下周一统一更新。
        </Alert>
      )}

      {data && groups.length > 0 && !denial && (
        <SpaceBetween size="l">
          {groups.map((g) => (
            <div className="radar-section__group" key={g.topicId}>
              <Box variant="h3" fontSize="heading-s" fontWeight="normal">
                {g.topicTitle}
              </Box>
              <div className="radar-section__grid">
                {g.directions.map((d) => (
                  <RadarCard key={d.directionId ?? d.name} direction={d} onOpen={onOpenDirection} />
                ))}
              </div>
            </div>
          ))}
        </SpaceBetween>
      )}
    </div>
  );
}
